import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import axiosRetry from 'axios-retry';

export interface TransportOptions {
  baseUrl: string;
}

type AccessTokenGetter = () => string | null | undefined | Promise<string | null | undefined>;
type LocaleGetter = () => string | undefined | Promise<string | undefined>;
type AuthErrorHandler = (error: AxiosError) => void | Promise<void>;

export class Transport {
  private readonly axios: AxiosInstance;
  private accessTokenGetter?: AccessTokenGetter;
  private authErrorHandler?: AuthErrorHandler;
  private localeGetter?: LocaleGetter;

  constructor(options: TransportOptions) {
    this.axios = axios.create({
      baseURL: options.baseUrl,
    });

    axiosRetry(this.axios, { retries: 3 });

    this.axios.interceptors.request.use(async (config) => {
      if (!config._skipAuth && this.accessTokenGetter) {
        const accessToken = await this.accessTokenGetter();

        if (accessToken) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      if (this.localeGetter) {
        const locale = await this.localeGetter();
        if (locale) {
          config.headers = config.headers ?? {};
          config.headers['Accept-Language'] = locale;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(undefined, async (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401 && this.authErrorHandler) {
        await this.authErrorHandler(error);
      }

      return Promise.reject(error);
    });
  }

  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axios.get<T>(url, config);
  }

  post<T = unknown>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axios.post<T>(url, data, config);
  }

  put<T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) {
    return this.axios.put<T>(url, data, config);
  }

  patch<T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) {
    return this.axios.patch<T>(url, data, config);
  }

  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axios.delete<T>(url, config);
  }

  setAccessTokenGetter(getter: AccessTokenGetter) {
    this.accessTokenGetter = getter;
  }

  setLocaleGetter(getter: LocaleGetter) {
    this.localeGetter = getter;
  }

  setAuthErrorHandler(handler: AuthErrorHandler) {
    this.authErrorHandler = handler;
  }
}
